import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Bot,
  Search,
  CheckCircle2,
  XCircle,
  HelpCircle,
  ShieldCheck,
  Zap,
  ArrowRight,
  Clock,
  FileText,
  Video,
  Gift,
  Send,
  Loader2,
  ChevronDown,
  PhoneCall,
  MessageSquare,
  AlertTriangle,
  Layers,
  BarChart3,
  Cpu,
  Globe,
  Code,
  Lock,
  ExternalLink,
  Award,
  Check,
  Copy,
  Wrench,
  BookOpen,
  Presentation,
  Download
} from "lucide-react";
import Navbar from "@/components/portfolio/Navbar";
import Footer from "@/components/portfolio/Footer";
import DemoAuditPresentationSection from "./DemoAuditPresentationSection";

export default function AiVisibilityAuditPage() {
  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    website: "",
    whatsapp: "",
    serviceNiche: "",
    specificQuestion: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [expandedDeliverables, setExpandedDeliverables] = useState({});
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  const deliverablesList = [
    {
      id: 1,
      title: "1. Complete Website AI Checkup (Shows Exactly How to Improve)",
      eyebrow: "Checklist Item 01",
      badge: "72-Hour Turnaround",
      color: "blue",
      icon: FileText,
      summary:
        "We thoroughly inspect your website to discover why AI engines like ChatGPT, Claude, and Gemini aren't mentioning you today. You get a clear, prioritized list of fixes your developer can apply directly.",
      tags: ["Full Website Review", "Step-by-Step Fixes for Developers"]
    },
    {
      id: 2,
      title: "2. Top Content Topics Custom-Built for Your Products & Business",
      eyebrow: "Checklist Item 02",
      badge: "Built for What You Sell",
      color: "cyan",
      icon: Layers,
      summary:
        "We give you high-priority content topics designed specifically around what you sell. When potential customers search AI for solutions in your niche, your website will have the exact answers they quote.",
      tags: ["Custom to Your Products", "High-Intent Customer Queries"]
    },
    {
      id: 3,
      title: "3. Two 1-on-1 Calls: 15-Min Prep Call & 30-Min Results Walkthrough",
      eyebrow: "Checklist Item 03",
      badge: "Personal Walkthrough",
      color: "emerald",
      icon: PhoneCall,
      summary:
        "We start with a quick 15-minute call to align on your business goals and products. Once the audit is done, we jump on a dedicated 30-minute Google Meet call to explain where your website stands, walk through the fixes, and answer questions.",
      tags: ["15m Pre-Audit Intake", "30m Handover Strategy Call"]
    },
    {
      id: 4,
      title: "4. Clear Pre-Publishing Checklist for Your Team",
      eyebrow: "Checklist Item 04",
      badge: "Easy Team Standard",
      color: "purple",
      icon: CheckCircle2,
      summary:
        "A simple, practical checklist your content writer or marketing team can check before publishing any new article or webpage to make sure AI search engines can easily read, index, and cite it.",
      tags: ["Editorial Quality Standard", "Check Before You Publish"]
    },
    {
      id: 5,
      title: "5. Prompts to Discover Content Ideas & Do AI Research on Your Own",
      eyebrow: "Checklist Item 05",
      badge: "Self-Serve Intelligence",
      color: "indigo",
      icon: Sparkles,
      summary:
        "A collection of proven prompts that empower your team to brainstorm high-converting content ideas and independently test your AI visibility anytime in the future without paying an agency.",
      tags: ["Content Idea Mining", "Reusable AI Workflows"]
    },
    {
      id: 6,
      title: "6. 20 to 30 Prompts to Track Your Growth in AI Search Over Time",
      eyebrow: "Checklist Item 06",
      badge: "Long-Term Benchmarking",
      color: "amber",
      icon: BarChart3,
      summary:
        "The exact 20–30 search prompts your potential buyers ask AI platforms. Track these prompts monthly in ChatGPT, Claude, and Gemini to watch your brand's citations and visibility grow.",
      tags: ["20–30 Benchmark Queries", "Monthly Tracking System"]
    }
  ];

  const deliverableStyles = {
    blue: {
      borderHover: "hover:border-blue-500/40",
      borderActive: "border-blue-500/50 shadow-[0_0_25px_rgba(59,130,246,0.12)]",
      dotBorder: "border-blue-400",
      dotRing: "ring-blue-500/20",
      dotBg: "bg-blue-400",
      iconBox: "bg-blue-500/15 text-blue-400 border-blue-500/25",
      eyebrow: "text-blue-300 bg-blue-500/10 border-blue-500/20",
      tag1: "bg-blue-950/60 border-blue-500/20 text-blue-300"
    },
    cyan: {
      borderHover: "hover:border-cyan-500/40",
      borderActive: "border-cyan-500/50 shadow-[0_0_25px_rgba(6,182,212,0.12)]",
      dotBorder: "border-cyan-400",
      dotRing: "ring-cyan-500/20",
      dotBg: "bg-cyan-400",
      iconBox: "bg-cyan-500/15 text-cyan-400 border-cyan-500/25",
      eyebrow: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
      tag1: "bg-cyan-950/60 border-cyan-500/20 text-cyan-300"
    },
    emerald: {
      borderHover: "hover:border-emerald-500/40",
      borderActive: "border-emerald-500/50 shadow-[0_0_25px_rgba(16,185,129,0.12)]",
      dotBorder: "border-emerald-400",
      dotRing: "ring-emerald-500/20",
      dotBg: "bg-emerald-400",
      iconBox: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
      eyebrow: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
      tag1: "bg-emerald-950/60 border-emerald-500/20 text-emerald-300"
    },
    purple: {
      borderHover: "hover:border-purple-500/40",
      borderActive: "border-purple-500/50 shadow-[0_0_25px_rgba(168,85,247,0.12)]",
      dotBorder: "border-purple-400",
      dotRing: "ring-purple-500/20",
      dotBg: "bg-purple-400",
      iconBox: "bg-purple-500/15 text-purple-400 border-purple-500/25",
      eyebrow: "text-purple-300 bg-purple-500/10 border-purple-500/20",
      tag1: "bg-purple-950/60 border-purple-500/20 text-purple-300"
    },
    indigo: {
      borderHover: "hover:border-indigo-500/40",
      borderActive: "border-indigo-500/50 shadow-[0_0_25px_rgba(99,102,241,0.12)]",
      dotBorder: "border-indigo-400",
      dotRing: "ring-indigo-500/20",
      dotBg: "bg-indigo-400",
      iconBox: "bg-indigo-500/15 text-indigo-400 border-indigo-500/25",
      eyebrow: "text-indigo-300 bg-indigo-500/10 border-indigo-500/20",
      tag1: "bg-indigo-950/60 border-indigo-500/20 text-indigo-300"
    },
    amber: {
      borderHover: "hover:border-amber-500/40",
      borderActive: "border-amber-500/50 shadow-[0_0_25px_rgba(245,158,11,0.12)]",
      dotBorder: "border-amber-400",
      dotRing: "ring-amber-500/20",
      dotBg: "bg-amber-400",
      iconBox: "bg-amber-500/15 text-amber-400 border-amber-500/25",
      eyebrow: "text-amber-300 bg-amber-500/10 border-amber-500/20",
      tag1: "bg-amber-950/60 border-amber-500/20 text-amber-300"
    }
  };

  const toggleDeliverable = (id) => {
    setExpandedDeliverables((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const allDeliverableIds = [1, 2, 3, 4, 5, 6];
  const allDeliverablesExpanded = allDeliverableIds.every((id) => !!expandedDeliverables[id]);
  const toggleAllDeliverables = () => {
    if (allDeliverablesExpanded) {
      setExpandedDeliverables({});
    } else {
      const next = {};
      allDeliverableIds.forEach((id) => {
        next[id] = true;
      });
      setExpandedDeliverables(next);
    }
  };

  // SEO Meta, Title, Open Graph and Structured Data Setup
  useEffect(() => {
    const originalTitle = document.title;
    document.title = "Website AI Visibility Audit (₹5,000) - Get Cited by ChatGPT & Claude | Kanna Narendra Kumar";

    // Meta Description
    const metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc ? metaDesc.getAttribute("content") : "";
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Get a comprehensive Website AI Visibility Audit for ₹5,000 with a 72-hour turnaround. Discover why ChatGPT, Claude, and Gemini skip your site and get developer-ready fixes + custom content topics."
      );
    }

    // Canonical Link Tag
    let canonical = document.querySelector('link[rel="canonical"]');
    let originalCanonical = canonical ? canonical.getAttribute("href") : null;
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    canonical.setAttribute("href", "https://narendradigital.in/ai-visibility-audit/");

    // Open Graph Meta Tags Helper
    const setOgTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
      return tag;
    };

    setOgTag("og:title", "Website AI Visibility Audit (₹5,000) - Get Cited by ChatGPT & Claude");
    setOgTag(
      "og:description",
      "Discover why AI search engines skip your site. Complete 4-layer audit, developer fixes blueprint, 20-30 tracking prompts, and 2 private walkthrough calls for ₹5,000."
    );
    setOgTag("og:url", "https://narendradigital.in/ai-visibility-audit/");
    setOgTag("og:type", "website");
    setOgTag("og:image", "https://narendradigital.in/ai-visibility-audit-og.jpg");

    // Twitter Meta Tags Helper
    const setTwitterTag = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
      return tag;
    };

    setTwitterTag("twitter:card", "summary_large_image");
    setTwitterTag("twitter:title", "Website AI Visibility Audit (₹5,000) - Get Cited by ChatGPT & Claude");
    setTwitterTag(
      "twitter:description",
      "Discover why AI search engines skip your site. Complete 4-layer audit, developer fixes blueprint, 20-30 tracking prompts, and 2 private walkthrough calls for ₹5,000."
    );
    setTwitterTag("twitter:image", "https://narendradigital.in/ai-visibility-audit-og.jpg");

    // JSON-LD Structured Data (Service + FAQPage + BreadcrumbList)
    const jsonLdData = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "WebPage",
            "@id": "https://narendradigital.in/ai-visibility-audit/#webpage",
            "url": "https://narendradigital.in/ai-visibility-audit/",
            "name": "Website AI Visibility & LLM Readiness Audit (₹5,000) | Kanna Narendra Kumar",
            "description": "Comprehensive Website AI Visibility Audit for ₹5,000 with a 72-hour turnaround. Discover why ChatGPT, Claude, and Gemini skip your site and get developer-ready fixes + custom content topics.",
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://narendradigital.in/"
                    },
                    {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "AI Visibility Audit",
                        "item": "https://narendradigital.in/ai-visibility-audit/"
                    }
                ]
            }
        },
        {
            "@type": "ProfessionalService",
            "@id": "https://narendradigital.in/ai-visibility-audit/#service",
            "name": "Website AI Visibility Audit & LLM Readiness Check",
            "url": "https://narendradigital.in/ai-visibility-audit/",
            "image": "https://narendradigital.in/narendra-workstation.jpg",
            "provider": {
                "@type": "Person",
                "@id": "https://narendradigital.in/#author",
                "name": "Kanna Narendra Kumar",
                "jobTitle": "AEO/GEO & SEO Consultant",
                "telephone": "+918464094525",
                "url": "https://narendradigital.in/"
            },
            "priceRange": "₹5,000",
            "serviceType": "AI Search Optimization & LLM Visibility Audit",
            "description": "Comprehensive 4-layer audit to verify why ChatGPT, Claude, and Gemini skip your site. Includes 6 deliverables: full website checkup, custom content topics, two 1-on-1 strategy calls, pre-publishing checklist, and 20-30 tracking prompts.",
            "offers": {
                "@type": "Offer",
                "price": "5000",
                "priceCurrency": "INR",
                "availability": "https://schema.org/InStock",
                "url": "https://narendradigital.in/ai-visibility-audit/",
                "validFrom": "2026-01-01",
                "priceValidUntil": "2026-12-31"
            }
        },
        {
            "@type": "FAQPage",
            "@id": "https://narendradigital.in/ai-visibility-audit/#faq",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "What will I get with this audit?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "You receive a complete 6-part deliverable package: 1) Full Website AI Checkup with step-by-step developer fixes, 2) Top Product Content Topics custom-built for what you sell, 3) Two 1-on-1 Calls (15-min prep call + 30-min Google Meet handover walkthrough), 4) Pre-Publishing Checklist for your writers, 5) Content Discovery & AI Research Prompts, and 6) 20 to 30 Growth Tracking Prompts to monitor visibility in LLMs over time."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Can I see a sample or demo audit report before ordering?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Yes! You can download our full 10-slide Demo Audit Presentation (.pptx) directly on this page to see the exact diagnostic format, baseline scorecard, developer-ready fix blueprints, and 30-prompt tracking matrix we deliver."
                    }
                },
                {
                    "@type": "Question",
                    "name": "How to pay?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "You can pay directly to Narendra via PhonePe (UPI / QR Code / Bank transfer). Payment details are provided once we connect and confirm your website intake. You only pay after we talk and align on your website goals."
                    }
                },
                {
                    "@type": "Question",
                    "name": "How long will it take to give results?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Guaranteed 72-hour turnaround. Your 72-hour countdown begins right after our 15-minute prep call is completed and intake is confirmed."
                    }
                },
                {
                    "@type": "Question",
                    "name": "How to give access to tools?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Will discuss in mail. You only need to share read-only (Viewer) access to Google Search Console, GA4, or server logs—no admin permissions needed."
                    }
                },
                {
                    "@type": "Question",
                    "name": "How will the 15-minute call happen?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "I will send a Google Meet invite link to your email/WhatsApp to coordinate a quick, convenient 15-minute slot to understand your core products and target audience."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Will I get an invoice?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Yes, a basic invoice will be provided for your accounting and business records."
                    }
                },
                {
                    "@type": "Question",
                    "name": "If I have any doubts in the report?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "You can connect with me directly through phone or WhatsApp (+91 8464094525). If I am busy on another client call, I will promptly drop you a Google Meet invite link to walk through your questions."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Do you implement the fixes on my website, or do I?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "I provide the complete diagnostic report, prioritized action plan, and exact developer instructions & Schema templates, and walk through them on our 30-minute Google Meet call. Your developer can implement the changes quickly using my step-by-step guide."
                    }
                }
            ]
        }
    ]
};
    let scriptTag = document.getElementById("ai-audit-jsonld");
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = "ai-audit-jsonld";
      scriptTag.type = "application/ld+json";
      document.head.appendChild(scriptTag);
    }
    scriptTag.text = JSON.stringify(jsonLdData);

    // Scroll listener for sticky CTA
    const handleScroll = () => {
      const formEl = document.getElementById("order-audit-form");
      if (formEl) {
        const rect = formEl.getBoundingClientRect();
        // Hide sticky bar when order form is in view so it never obstructs inputs or buttons
        if (rect.top <= window.innerHeight - 80 && rect.bottom >= 100) {
          setShowStickyBar(false);
          return;
        }
      }
      if (window.scrollY > 600) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.title = originalTitle;
      if (metaDesc && originalDesc) {
        metaDesc.setAttribute("content", originalDesc);
      }
      if (createdCanonical && canonical) {
        canonical.remove();
      } else if (canonical && originalCanonical) {
        canonical.setAttribute("href", originalCanonical);
      }
      const existingScript = document.getElementById("ai-audit-jsonld");
      if (existingScript) existingScript.remove();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const blockersScrollRef = useRef(null);
  const isBlockerInteractingRef = useRef(false);

  const roadmapScrollRef = useRef(null);
  const isRoadmapInteractingRef = useRef(false);
  const [activeRoadmapIndex, setActiveRoadmapIndex] = useState(0);

  // Auto-scroll the 4 Essential Questions Roadmap cards horizontally on mobile with smooth transitions
  useEffect(() => {
    const el = roadmapScrollRef.current;
    if (!el) return;

    // Track active card index via scroll listener
    const handleScroll = () => {
      if (el.scrollWidth <= el.clientWidth + 20) return;
      const firstCard = el.firstElementChild;
      const cardStep = firstCard ? firstCard.offsetWidth + 14 : 284;
      const index = Math.round(el.scrollLeft / cardStep);
      setActiveRoadmapIndex(Math.min(Math.max(index, 0), 3));
    };
    el.addEventListener("scroll", handleScroll, { passive: true });

    const interval = setInterval(() => {
      if (isRoadmapInteractingRef.current) return;
      // Only auto-scroll when content overflows horizontally (mobile viewports)
      if (el.scrollWidth > el.clientWidth + 20) {
        const firstCard = el.firstElementChild;
        const cardStep = firstCard ? firstCard.offsetWidth + 14 : 284;
        const maxScroll = el.scrollWidth - el.clientWidth;

        if (el.scrollLeft >= maxScroll - 15) {
          el.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          el.scrollBy({ left: cardStep, behavior: "smooth" });
        }
      }
    }, 3200);

    return () => {
      clearInterval(interval);
      el.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Auto-scroll the 4 Core Website Blockers horizontally on mobile
  useEffect(() => {
    const el = blockersScrollRef.current;
    if (!el) return;

    const interval = setInterval(() => {
      if (isBlockerInteractingRef.current) return;
      // Only auto-scroll when content overflows horizontally (mobile viewports)
      if (el.scrollWidth > el.clientWidth + 20) {
        const firstCard = el.firstElementChild;
        const cardStep = firstCard ? firstCard.offsetWidth + 16 : 300;
        const maxScroll = el.scrollWidth - el.clientWidth;

        if (el.scrollLeft >= maxScroll - 15) {
          el.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          el.scrollBy({ left: cardStep, behavior: "smooth" });
        }
      }
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      name: formData.name,
      email: formData.email,
      company: formData.website,
      phone: formData.whatsapp,
      needs: "AI Visibility Audit (₹5,000)",
      message: `[AI VISIBILITY AUDIT ORDER - ₹5,000]
Website / Domain: ${formData.website}
WhatsApp/Phone: ${formData.whatsapp}
Core Service/Product: ${formData.serviceNiche}
Specific Notes: ${formData.specificQuestion || "None"}
Deliverables: 1) Full AI Visibility Audit 2) Product-Tailored Topics 3) Pre & Handover Calls (15m+30m) 4) Pre-Publishing Checklist 5) Idea Mining Prompts 6) 20-30 Tracking Prompts`,
      createdAt: new Date().toISOString()
    };

    try {
      // 1. Post to API backend
      const res = await fetch("/api/audit-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        console.warn("Backend API response status:", res.status);
      }

      // 2. Client-side localStorage backup
      try {
        const existing = JSON.parse(localStorage.getItem("narendra_audit_requests_backup") || "[]");
        existing.unshift({ ...payload, id: `audit_order_${Date.now()}` });
        localStorage.setItem("narendra_audit_requests_backup", JSON.stringify(existing));
      } catch (err) {
        console.warn("LocalStorage backup warning:", err);
      }

      setSubmittedData(payload);
      setIsSubmitted(true);
    } catch (err) {
      console.warn("Network error during submission:", err);
      try {
        const existing = JSON.parse(localStorage.getItem("narendra_audit_requests_backup") || "[]");
        existing.unshift({ ...payload, id: `offline_audit_${Date.now()}` });
        localStorage.setItem("narendra_audit_requests_backup", JSON.stringify(existing));
      } catch (localErr) {
        console.warn("Storage warning:", localErr);
      }
      setSubmittedData(payload);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const el = document.getElementById("order-audit-form");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const copyPhoneNumber = () => {
    navigator.clipboard.writeText("+91 8464094525");
    setCopiedPhone(true);
    setTimeout(() => setCopiedPhone(false), 2500);
  };

  return (
    <div className="min-h-screen bg-[#070a12] text-[#f8fafc] font-sans selection:bg-blue-600 selection:text-white relative overflow-hidden">
      {/* Background Ambient Glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[850px] h-[500px] bg-gradient-to-b from-blue-600/15 via-cyan-500/10 to-transparent blur-[140px]" />
        <div className="absolute top-[35%] right-[-10%] w-[550px] h-[550px] bg-purple-600/10 blur-[150px]" />
        <div className="absolute bottom-[20%] left-[-10%] w-[550px] h-[550px] bg-cyan-600/10 blur-[150px]" />
      </div>

      <Navbar />

      <main className="relative z-10 pt-20 sm:pt-28 pb-28 sm:pb-36">
        {/* =========================================================
            HERO SECTION
           ========================================================= */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-8 pb-10 sm:pb-16 text-center">
          {/* Eyebrow Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-mono uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
            <span>AI Visibility &amp; LLM Readiness Audit</span>
          </div>

          {/* Primary Main Headline */}
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white tracking-tight leading-[1.15] sm:leading-[1.12] max-w-4xl mx-auto font-heading">
            Is Your Website Ready to Be{" "}
            <span className="bg-gradient-to-r from-cyan-400 via-sky-300 to-blue-500 bg-clip-text text-transparent">
              Showcased &amp; Cited by LLMs?
            </span>
          </h1>

          {/* Subheadline & Direct Pitch */}
          <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            I audit your website&apos;s AI visibility and technical readiness, deliver a prioritized <strong className="text-white">Key Fixes Blueprint</strong> for your team to implement, and include a <strong className="text-cyan-300">Free AI Content Plan</strong>.
          </p>

          {/* Value Highlights Pill Row */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5 text-xs text-slate-300">
            <span className="px-3 py-1.5 rounded-lg bg-slate-900/80 border border-white/10 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              72-Hour Turnaround
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-slate-900/80 border border-white/10 flex items-center gap-1.5 text-cyan-300 font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
              Technical &amp; Content Audit
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-slate-900/80 border border-white/10 flex items-center gap-1.5">
              <Wrench className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
              Key Fixes for Your Developer
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 flex items-center gap-1.5 font-medium">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              Pay Only After We Talk
            </span>
          </div>

          {/* CTA Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3.5">
            <button
              onClick={scrollToForm}
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-heading font-bold text-base shadow-[0_0_35px_rgba(37,99,235,0.4)] hover:shadow-[0_0_45px_rgba(6,182,212,0.6)] transition-all duration-200 transform hover:-translate-y-0.5 flex items-center justify-center gap-2 group cursor-pointer"
            >
              <span>Order AI Visibility Audit — ₹5,000</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>

            <a
              href="#demo-audit-report"
              className="w-full sm:w-auto px-6 py-4 rounded-xl bg-slate-900/90 hover:bg-slate-800 border border-cyan-500/40 hover:border-cyan-400 text-cyan-300 hover:text-white font-heading font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-cyan-950/30 cursor-pointer"
            >
              <Presentation className="w-4 h-4 text-cyan-400" />
              <span>Preview Demo Audit (PPT)</span>
            </a>
          </div>

          {/* Prominent High-Visibility Narendra Author & Trust Card */}
          <div className="mt-10 max-w-xl mx-auto p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-blue-950/60 via-slate-900/95 to-cyan-950/60 border border-cyan-500/40 shadow-[0_0_35px_rgba(6,182,212,0.2)] flex items-center gap-4 text-left backdrop-blur-xl">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-600 to-cyan-400 text-white flex items-center justify-center font-black text-lg shadow-lg shrink-0 ring-2 ring-cyan-400/40">
              KN
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-cyan-300 font-bold">
                <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>100% Hand-Audited by Narendra</span>
              </div>
              <p className="text-base sm:text-lg font-black text-white tracking-tight mt-0.5 font-heading">
                Kanna Narendra Kumar
              </p>
              <p className="text-xs sm:text-sm text-cyan-300/90 font-medium">
                AEO/GEO & SEO Consultant
              </p>
            </div>
            <div className="hidden sm:flex flex-col items-end shrink-0 pl-3 border-l border-white/10">
              <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[11px] font-mono font-bold whitespace-nowrap">
                No Automated Tools
              </span>
              <span className="text-[10px] text-emerald-400 font-mono mt-1 font-semibold">
                30-Min Google Meet Call
              </span>
            </div>
          </div>
        </section>

        {/* =========================================================
            THE 4 ESSENTIAL QUESTIONS ROADMAP
           ========================================================= */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div
            ref={roadmapScrollRef}
            onTouchStart={() => {
              isRoadmapInteractingRef.current = true;
            }}
            onTouchEnd={() => {
              setTimeout(() => {
                isRoadmapInteractingRef.current = false;
              }, 4000);
            }}
            onMouseEnter={() => {
              isRoadmapInteractingRef.current = true;
            }}
            onMouseLeave={() => {
              isRoadmapInteractingRef.current = false;
            }}
            className="flex sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4 overflow-x-auto sm:overflow-visible no-scrollbar pb-2 sm:pb-0 snap-x snap-mandatory scroll-smooth -mx-4 px-4 sm:mx-0 sm:px-0"
          >
            <a
              href="#question-3"
              className="w-[270px] sm:w-auto shrink-0 snap-start p-5 rounded-2xl bg-slate-900/60 border border-white/10 hover:border-emerald-500/40 hover:bg-slate-900/90 transition-all duration-200 group flex flex-col justify-between"
            >
              <div>
                <span className="text-xs font-mono font-bold text-emerald-400">01. THE VALUE</span>
                <h3 className="text-sm font-bold text-white mt-1 group-hover:text-emerald-300 transition-colors">
                  What am I getting for ₹5,000?
                </h3>
                <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">
                  Full audit, custom topics, 2 calls (15m + 30m), content checklist &amp; 20–30 tracking prompts.
                </p>
              </div>
              <span className="text-[11px] text-emerald-400/80 font-mono mt-3 flex items-center gap-1">
                See 6 Deliverables <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </a>

            <a
              href="#question-1"
              className="w-[270px] sm:w-auto shrink-0 snap-start p-5 rounded-2xl bg-slate-900/60 border border-white/10 hover:border-cyan-500/40 hover:bg-slate-900/90 transition-all duration-200 group flex flex-col justify-between"
            >
              <div>
                <span className="text-xs font-mono font-bold text-cyan-400">02. THE NEED</span>
                <h3 className="text-sm font-bold text-white mt-1 group-hover:text-cyan-300 transition-colors">
                  Am I visible in AI search?
                </h3>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                  Direct testing to verify if AI crawlers can discover and cite your domain.
                </p>
              </div>
              <span className="text-[11px] text-cyan-400/80 font-mono mt-3 flex items-center gap-1">
                Explore The Need <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </a>

            <a
              href="#question-2"
              className="w-[270px] sm:w-auto shrink-0 snap-start p-5 rounded-2xl bg-slate-900/60 border border-white/10 hover:border-purple-500/40 hover:bg-slate-900/90 transition-all duration-200 group flex flex-col justify-between"
            >
              <div>
                <span className="text-xs font-mono font-bold text-purple-400">03. THE PROBLEM</span>
                <h3 className="text-sm font-bold text-white mt-1 group-hover:text-purple-300 transition-colors">
                  Why is my site missing?
                </h3>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                  Pinpoints bot crawl blocks, audience intent gaps, and entity disconnect.
                </p>
              </div>
              <span className="text-[11px] text-purple-400/80 font-mono mt-3 flex items-center gap-1">
                Diagnose Website Issues <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </a>

            <a
              href="#question-4"
              className="w-[270px] sm:w-auto shrink-0 snap-start p-5 rounded-2xl bg-slate-900/60 border border-white/10 hover:border-sky-500/40 hover:bg-slate-900/90 transition-all duration-200 group flex flex-col justify-between"
            >
              <div>
                <span className="text-xs font-mono font-bold text-sky-400">04. TRUST & TIMELINE</span>
                <h3 className="text-sm font-bold text-white mt-1 group-hover:text-sky-300 transition-colors">
                  Why trust you & what&apos;s next?
                </h3>
                <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">
                  Fast 72h turnaround with a 1-on-1 walkthrough—pay only after we talk.
                </p>
              </div>
              <span className="text-[11px] text-sky-400/80 font-mono mt-3 flex items-center gap-1">
                View Process <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </a>
          </div>

          {/* Mobile Dot Indicators for Roadmap */}
          <div className="flex sm:hidden items-center justify-center gap-1.5 mt-3">
            {[0, 1, 2, 3].map((idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  const el = roadmapScrollRef.current;
                  if (!el) return;
                  const firstCard = el.firstElementChild;
                  const cardStep = firstCard ? firstCard.offsetWidth + 14 : 284;
                  el.scrollTo({ left: idx * cardStep, behavior: "smooth" });
                }}
                aria-label={`Scroll to question ${idx + 1}`}
                className={`transition-all duration-300 rounded-full h-1.5 ${
                  activeRoadmapIndex === idx
                    ? "w-6 bg-cyan-400"
                    : "w-1.5 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </section>

        {/* =========================================================
            QUESTION 3 (POSITION 1): WHAT AM I GETTING FOR ₹5,000?
           ========================================================= */}
        <section id="question-3" className="max-w-6xl mx-auto px-3.5 sm:px-6 lg:px-8 py-12 sm:py-16 scroll-mt-24">
          <div className="bg-[#0b0f19] border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-8 md:p-10 relative overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 sm:pb-8 border-b border-white/[0.08]">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-emerald-500/10 text-emerald-400 text-xs font-mono uppercase tracking-wider mb-2">
                  <span>Question 01</span>
                  <span>•</span>
                  <span>Transparent Value & Scope</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-heading">
                  What am I getting for ₹5,000?
                </h2>
                <p className="text-slate-300 text-sm sm:text-base mt-2 max-w-2xl leading-relaxed">
                  What exactly will the audit check, what findings will I receive, and what actionable recommendations will help me improve my AI visibility?
                </p>
              </div>

              {/* Price Tag Box - Sleek banner on mobile, stacked badge on desktop */}
              <div className="w-full md:w-auto p-4 sm:p-5 rounded-2xl bg-gradient-to-r md:bg-gradient-to-br from-emerald-500/15 via-cyan-500/10 to-emerald-500/15 border border-emerald-500/30 flex items-center justify-between md:flex-col md:text-right md:items-end gap-3 shadow-[0_0_20px_rgba(16,185,129,0.1)]">
                <div>
                  <span className="text-[10px] sm:text-[11px] font-mono uppercase tracking-wider text-emerald-400 font-bold block">Total Investment</span>
                  <span className="text-[11px] sm:text-xs text-slate-300 font-mono block mt-0.5">One-time fee · No hidden costs</span>
                </div>
                <div className="text-right">
                  <span className="text-2xl sm:text-3xl font-extrabold text-white font-heading tracking-tight block">₹5,000</span>
                </div>
              </div>
            </div>

            {/* Clear Role Disclaimer */}
            <div className="mt-5 sm:mt-6 p-3.5 sm:p-4 rounded-xl bg-blue-950/40 border border-blue-500/30 flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-blue-500/15 border border-blue-500/25 shrink-0 text-cyan-400 mt-0.5">
                <Wrench className="w-4 h-4" />
              </div>
              <div className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                <strong className="text-white">Clear Scope &amp; Execution:</strong> I provide the in-depth Website Audit, the exact Key Fixes blueprint, and the Content Plan. <strong>Your in-house team or developer will implement the changes</strong> on your website using my step-by-step instructions and Schema templates.
              </div>
            </div>

            {/* Action Bar: Instructions & Expand/Collapse All */}
            <div className="flex items-center justify-between mt-6 sm:mt-8 mb-3 px-1 gap-2">
              <div className="text-xs font-mono text-slate-400 flex items-center gap-1.5 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse shrink-0"></span>
                <span className="truncate hidden sm:inline">Click any item or arrow to inspect what&apos;s inside:</span>
                <span className="truncate sm:hidden">Tap item to inspect:</span>
              </div>
              <button
                type="button"
                onClick={toggleAllDeliverables}
                className="text-xs font-mono text-cyan-400 hover:text-cyan-300 transition-colors font-medium flex items-center gap-1 hover:underline focus:outline-none shrink-0 whitespace-nowrap"
              >
                <span>{allDeliverablesExpanded ? "Collapse All" : "Expand All"}</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    allDeliverablesExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>

            {/* The 6 Deliverables Checklist (Timeline Rail Style - Collapsible) */}
            <div className="relative border-l-2 border-emerald-500/30 ml-2.5 sm:ml-7 pl-3.5 sm:pl-8 space-y-3">
              {deliverablesList.map((item, idx) => {
                const isExpanded = !!expandedDeliverables[item.id];
                const styles = deliverableStyles[item.color] || deliverableStyles.blue;
                const IconComp = item.icon;

                return (
                  <div key={item.id} className="relative group">
                    {/* Connector Dot */}
                    <div
                      className={`absolute -left-[23px] sm:-left-[39px] top-4 sm:top-5 w-4 h-4 rounded-full border-2 ${styles.dotBorder} bg-[#070a12] flex items-center justify-center ring-4 ${styles.dotRing} group-hover:scale-110 transition-all`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${styles.dotBg} ${idx === 0 ? "animate-pulse" : ""}`}
                      ></span>
                    </div>

                    {/* Card */}
                    <div
                      onClick={() => toggleDeliverable(item.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleDeliverable(item.id);
                        }
                      }}
                      className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all cursor-pointer select-none ${
                        isExpanded
                          ? `bg-[#0e162a] ${styles.borderActive}`
                          : `bg-[#0a0f1d]/85 border border-white/[0.08] ${styles.borderHover} hover:bg-[#0e162a]`
                      }`}
                    >
                      {/* Compact Header Row */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center sm:items-start gap-3 min-w-0 flex-1">
                          <div
                            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${styles.iconBox} flex items-center justify-center shrink-0`}
                          >
                            <IconComp className="w-4 h-4 sm:w-5 sm:h-5" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-0.5">
                              <span
                                className={`text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${styles.eyebrow}`}
                              >
                                {item.eyebrow}
                              </span>
                              <span className="text-slate-500 hidden sm:inline">•</span>
                              <span className="text-[11px] text-slate-400 font-mono hidden sm:inline">
                                {item.badge}
                              </span>
                            </div>
                            <h4 className="text-xs sm:text-base font-bold text-white font-heading leading-snug">
                              {item.title}
                            </h4>
                          </div>
                        </div>

                        {/* Right Side: Included Badge & Arrow Button */}
                        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                          <span className="hidden sm:inline-flex px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-mono font-bold items-center gap-1.5 shadow-[0_0_12px_rgba(16,185,129,0.15)]">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Included</span>
                          </span>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDeliverable(item.id);
                            }}
                            aria-label={isExpanded ? "Collapse item" : "Expand item"}
                            className={`w-8 h-8 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0 transition-all duration-200 ${
                              isExpanded
                                ? "rotate-180 bg-white/[0.1] text-cyan-300 border-cyan-400/50"
                                : "text-slate-400 group-hover:text-white group-hover:border-white/20"
                            }`}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Expandable Sub-Content */}
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-white/[0.06] pl-0 sm:pl-13 transition-all">
                          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed max-w-3xl">
                            {item.summary}
                          </p>
                          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px] font-mono">
                            <span className={`px-2.5 py-1 rounded-md ${styles.tag1}`}>
                              {item.tags[0]}
                            </span>
                            <span className="px-2.5 py-1 rounded-md bg-white/[0.04] border border-white/10 text-slate-300">
                              {item.tags[1]}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================
            DEMO AUDIT PRESENTATION (PPTX DOWNLOAD & PREVIEW)
           ========================================================= */}
        <DemoAuditPresentationSection onOrderClick={scrollToForm} />

        {/* =========================================================
            QUESTION 1 (POSITION 2): AM I VISIBLE IN AI SEARCH?
           ========================================================= */}
        <section id="question-1" className="max-w-6xl mx-auto px-3.5 sm:px-6 lg:px-8 py-12 scroll-mt-24">
          <div className="bg-[#0b0f19] border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-8 md:p-10 relative overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-white/[0.08]">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-cyan-500/10 text-cyan-400 text-xs font-mono uppercase tracking-wider mb-2">
                  <span>Question 02</span>
                  <span>•</span>
                  <span>The Buyer's Need</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-heading">
                  Am I visible in AI search?
                </h2>
                <p className="text-slate-300 text-sm sm:text-base mt-1.5 max-w-2xl leading-relaxed">
                  You rank well on Google, but when buyers ask ChatGPT, Claude, or Gemini for recommendations, your website is missing. Can LLMs even see your website?
                </p>
              </div>

              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 border border-white/10 text-xs font-mono self-start md:self-center">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping"></span>
                <span className="text-slate-300">LLM Search Readiness</span>
              </div>
            </div>

            {/* Clean 2-Column: Google vs LLMs + Visual Contrast */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8 items-center">
              {/* Left Column: Why Google Sees You But AI Doesn't */}
              <div className="hidden lg:block space-y-4">
                <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                  <Search className="w-5 h-5 text-cyan-400" />
                  <span>The Google vs. AI Search Disconnect</span>
                </h3>

                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  Google ranks pages based on keywords, backlinks, and queues JavaScript for hours. AI engines (ChatGPT, Claude, Gemini) don't rank links—they synthesize answers using live crawlers with a strict <strong>2–3 second timeout</strong>.
                </p>

                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  If your website relies on client-side JS hydration, blocks AI bots in firewalls, or lacks Schema entity graphs, <strong>the LLM sees an empty blank page and cites your competitors instead</strong>.
                </p>
              </div>

              {/* Right Column: Visual Contrast */}
              <div className="space-y-3.5">
                {/* Card 1: Unprepared */}
                <div className="p-4 sm:p-4.5 rounded-2xl bg-slate-950/80 border border-rose-500/25 transition-all">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <div className="w-6 h-6 rounded-lg bg-rose-500/15 border border-rose-500/30 flex items-center justify-center shrink-0 text-rose-400 mt-0.5">
                        <XCircle className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs sm:text-sm font-bold text-rose-300 font-heading leading-snug">
                          Unprepared
                          <span className="block text-[11px] sm:text-xs text-slate-400 font-normal mt-0.5">
                            Good on Google · Invisible to AI
                          </span>
                        </h4>
                      </div>
                    </div>
                    <span className="shrink-0 whitespace-nowrap text-[10px] sm:text-[11px] font-mono font-bold text-rose-300 px-2.5 py-1 rounded-full bg-rose-500/15 border border-rose-500/30">
                      0 Citations
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed pl-0 sm:pl-8.5">
                    AI bots can&apos;t read content on your website. This often happens because bots are blocked or content is hidden behind JavaScript rendering. Since bots can&apos;t see your pages, your competitors get recommended instead.
                  </p>
                </div>

                {/* Card 2: LLM-Ready */}
                <div className="p-4 sm:p-4.5 rounded-2xl bg-slate-950/80 border border-emerald-500/30 transition-all">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <div className="w-6 h-6 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0 text-emerald-400 mt-0.5">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs sm:text-sm font-bold text-emerald-300 font-heading leading-snug">
                          LLM-Ready Website
                          <span className="block text-[11px] sm:text-xs text-slate-400 font-normal mt-0.5">
                            Post-Audit Fixes Applied
                          </span>
                        </h4>
                      </div>
                    </div>
                    <span className="shrink-0 whitespace-nowrap text-[10px] sm:text-[11px] font-mono font-bold text-emerald-300 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30">
                      Showcased &amp; Cited
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed pl-0 sm:pl-8.5">
                    AI bots can instantly read your content and products with zero blocks or loading delays. When potential customers ask AI questions in your niche, your website gets quoted and cited.
                  </p>
                </div>
              </div>
            </div>

            {/* Full-Width Solution Strip Across Entire Section */}
            <div className="mt-6 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-cyan-950/40 via-slate-900/90 to-slate-950 border border-cyan-500/30 shadow-lg shadow-cyan-950/20 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start sm:items-center gap-3.5">
                <div className="p-2.5 rounded-xl bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 shrink-0 mt-0.5 sm:mt-0">
                  <Zap className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
                    <span className="px-2 py-0.5 rounded-md bg-cyan-500/15 border border-cyan-500/30 text-[10px] font-mono font-bold text-cyan-300 uppercase tracking-wider">
                      The Solution
                    </span>
                    <span className="text-[11px] font-mono text-slate-400">
                      72h Turnaround · ₹5,000 Flat
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-200 font-medium leading-relaxed">
                    I will find the key issues why AI can&apos;t see your website—and give your team the direct fixes to get cited.
                  </p>
                </div>
              </div>

              <button
                onClick={scrollToForm}
                className="w-full md:w-auto px-6 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-heading font-bold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(6,182,212,0.25)] hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all cursor-pointer group shrink-0"
              >
                <span>Find My Website&apos;s AI Issues</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </section>

        {/* =========================================================
            QUESTION 2 (POSITION 3): WHY IS MY WEBSITE MISSING FROM AI RESULTS?
           ========================================================= */}
        <section id="question-2" className="max-w-6xl mx-auto px-3.5 sm:px-6 lg:px-8 py-12 sm:py-16 scroll-mt-24">
          <div className="bg-[#0b0f19] border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-8 md:p-10 relative overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-8 border-b border-white/[0.08]">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-purple-500/10 text-purple-400 text-xs font-mono uppercase tracking-wider mb-2">
                  <span>Question 03</span>
                  <span>•</span>
                  <span>The Root-Cause Diagnostic</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-heading">
                  Why is my website missing from AI results?
                </h2>
                <p className="text-slate-300 text-sm sm:text-base mt-2 max-w-2xl">
                  What technical, content, authority, or business identity issues on your website are preventing AI platforms from discovering or recommending your business?
                </p>
              </div>

              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-500/15 border border-purple-500/30 text-purple-300 text-xs font-mono self-start md:self-center">
                <Cpu className="w-4 h-4 text-purple-400" />
                <span>4 Core Website Blockers</span>
              </div>
            </div>

            {/* The 4 Core Website Blockers Grid (2x2 on Desktop, Side-by-Side Auto-Scroll on Mobile) */}
            <div
              ref={blockersScrollRef}
              onTouchStart={() => {
                isBlockerInteractingRef.current = true;
              }}
              onTouchEnd={() => {
                setTimeout(() => {
                  isBlockerInteractingRef.current = false;
                }, 4000);
              }}
              onMouseEnter={() => {
                isBlockerInteractingRef.current = true;
              }}
              onMouseLeave={() => {
                isBlockerInteractingRef.current = false;
              }}
              className="flex md:grid md:grid-cols-2 gap-4 mt-8 overflow-x-auto md:overflow-visible no-scrollbar pb-3 md:pb-0 snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0"
            >
              {/* Blocker 1: Technical - Can Bots See Your Content? */}
              <div className="w-[285px] sm:w-[320px] md:w-auto shrink-0 snap-start p-5 rounded-2xl bg-slate-900/70 border border-white/10 hover:border-purple-500/30 transition-all duration-200 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="w-9 h-9 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center font-bold font-mono text-sm">
                      01
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-white">Technical: Can Bots See Your Content?</h3>
                      <p className="text-[11px] text-purple-300/80 font-mono">robots.txt • WAF • Raw HTML</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Firewalls, robots.txt disallows, or heavy client-side JavaScript block AI crawlers (<code className="text-purple-300 bg-purple-950/60 px-1 py-0.5 rounded">GPTBot</code>, <code className="text-purple-300 bg-purple-950/60 px-1 py-0.5 rounded">ClaudeBot</code>) from seeing your actual content.
                  </p>
                </div>
                <div className="mt-3.5 pt-2.5 border-t border-white/5 flex items-center gap-1.5 text-[11px] text-slate-400 font-mono">
                  <CheckCircle2 className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  <span>Audited: Bot access, render latency &amp; raw HTML</span>
                </div>
              </div>

              {/* Blocker 2: Intent - Is Content Serving Your Audience? */}
              <div className="w-[285px] sm:w-[320px] md:w-auto shrink-0 snap-start p-5 rounded-2xl bg-slate-900/70 border border-white/10 hover:border-purple-500/30 transition-all duration-200 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="w-9 h-9 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center font-bold font-mono text-sm">
                      02
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-white">Intent: Does It Serve Your Audience?</h3>
                      <p className="text-[11px] text-purple-300/80 font-mono">Search Intent • User Problems • Solutions</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Your content may rank for old keywords but fail to directly answer the specific conversational questions and decision-stage queries buyers ask AI models today.
                  </p>
                </div>
                <div className="mt-3.5 pt-2.5 border-t border-white/5 flex items-center gap-1.5 text-[11px] text-slate-400 font-mono">
                  <CheckCircle2 className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  <span>Audited: Conversational intent &amp; answer relevance</span>
                </div>
              </div>

              {/* Blocker 3: Value - Is Your Content Worth Citing? */}
              <div className="w-[285px] sm:w-[320px] md:w-auto shrink-0 snap-start p-5 rounded-2xl bg-slate-900/70 border border-white/10 hover:border-purple-500/30 transition-all duration-200 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="w-9 h-9 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center font-bold font-mono text-sm">
                      03
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-white">Value: Is Your Content Worth Citing?</h3>
                      <p className="text-[11px] text-purple-300/80 font-mono">Factual Density • Data • Authority</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    AI models skip marketing buzzwords. They cite websites that provide dense factual definitions, clear comparisons, and verifiable proof over generic sales fluff.
                  </p>
                </div>
                <div className="mt-3.5 pt-2.5 border-t border-white/5 flex items-center gap-1.5 text-[11px] text-slate-400 font-mono">
                  <CheckCircle2 className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  <span>Audited: Factual depth, structured data &amp; citation readiness</span>
                </div>
              </div>

              {/* Blocker 4: Entity - Do You Have Enough Entity Presence Over the Internet? */}
              <div className="w-[285px] sm:w-[320px] md:w-auto shrink-0 snap-start p-5 rounded-2xl bg-slate-900/70 border border-white/10 hover:border-purple-500/30 transition-all duration-200 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2.5">
                    <div className="w-9 h-9 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center font-bold font-mono text-sm">
                      04
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-white">Entity: Enough Presence Over the Internet?</h3>
                      <p className="text-[11px] text-purple-300/80 font-mono">Knowledge Graph • sameAs • Authority Footprint</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    LLMs evaluate your external footprint, Schema.org entity graphs, and trusted citations across the web to confirm your business is legitimate and recognized enough to recommend.
                  </p>
                </div>
                <div className="mt-3.5 pt-2.5 border-t border-white/5 flex items-center gap-1.5 text-[11px] text-slate-400 font-mono">
                  <CheckCircle2 className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                  <span>Audited: Entity disambiguation, Knowledge Graph &amp; web citations</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================
            QUESTION 4: WHY SHOULD I TRUST YOU & WHAT HAPPENS NEXT?
           ========================================================= */}
        <section id="question-4" className="max-w-6xl mx-auto px-3.5 sm:px-6 lg:px-8 py-12 sm:py-16 scroll-mt-24">
          <div className="bg-[#0b0f19] border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-8 md:p-10 relative overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-8 border-b border-white/[0.08]">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-sky-500/10 text-sky-400 text-xs font-mono uppercase tracking-wider mb-2">
                  <span>Question 04</span>
                  <span>•</span>
                  <span>Trust, Proof & Delivery Timeline</span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-heading">
                  Why should I trust you, and what happens next?
                </h2>
                <p className="text-slate-300 text-sm sm:text-base mt-2 max-w-2xl">
                  What is the audit methodology, what evidence will you provide, how long will delivery take, and how will your team know what to fix first?
                </p>
              </div>

              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500/15 border border-sky-500/30 text-sky-300 text-xs font-mono self-start md:self-center">
                <Clock className="w-4 h-4 text-sky-400" />
                <span>72-Hour Turnaround</span>
              </div>
            </div>

            {/* Why Trust Narendra: 3 Core Proof Pillars */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <div className="p-5 rounded-2xl bg-slate-900/60 border border-white/10">
                <div className="w-9 h-9 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center mb-3">
                  <Award className="w-5 h-5" />
                </div>
                <h4 className="text-sm font-bold text-white">8+ Years Enterprise Experience</h4>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  Proven enterprise track record scaling search architecture for global brands including Seismic (1.3M+ impressions) and MyOperator.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-slate-900/60 border border-white/10">
                <div className="w-9 h-9 rounded-lg bg-cyan-500/20 text-cyan-400 flex items-center justify-center mb-3">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <h4 className="text-sm font-bold text-white">100% Hand-Crafted Diagnostic</h4>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  No automated generic tool exports. Every domain is personally audited by Narendra, followed by a 30-min strategy call.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-slate-900/60 border border-white/10">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-3">
                  <Wrench className="w-5 h-5" />
                </div>
                <h4 className="text-sm font-bold text-white">Ready-to-Implement Fixes</h4>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  You receive exact Schema templates, technical blueprints, and step-by-step instructions so your developer can implement with ease.
                </p>
              </div>
            </div>

            {/* Zero-Risk Guarantee: Pay money only after you talk with me */}
            <div className="mt-8 p-5 rounded-2xl bg-emerald-950/40 border-2 border-emerald-500/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-[0_0_25px_rgba(16,185,129,0.15)]">
              <div className="flex items-start sm:items-center gap-3.5">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-sm sm:text-base font-bold text-white flex flex-wrap items-center gap-2">
                    <span>Zero-Risk Policy:</span>
                    <span className="text-emerald-400 font-extrabold underline underline-offset-4">Pay money only after you talk with me</span>
                  </h4>
                  <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                    No upfront payment required when submitting your website. Narendra connects with you on a 30-minute Google Meet call first to explain the audit scope and ensure it's the right fit for your website. You only pay ₹5,000 after we talk.
                  </p>
                </div>
              </div>
              <div className="shrink-0 self-start sm:self-center">
                <span className="text-xs font-mono text-emerald-300 bg-emerald-500/20 px-3.5 py-1.5 rounded-full border border-emerald-500/30 font-bold whitespace-nowrap block">
                  100% Risk Free
                </span>
              </div>
            </div>

            {/* What Happens Next: 4-Step Timeline */}
            <div className="mt-10 pt-8 border-t border-white/[0.08]">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-6">
                <Zap className="w-5 h-5 text-sky-400" />
                <span>The 4-Step Process &amp; Timeline:</span>
              </h3>

              {/* Connected Vertical Timeline Rail / String (Mobile & Desktop) */}
              <div className="relative space-y-4 md:space-y-4">
                {/* Continuous Glowing Timeline String / Rail */}
                <div 
                  className="absolute left-[15px] sm:left-[17px] top-6 bottom-6 w-0.5 bg-gradient-to-b from-cyan-400 via-emerald-400 to-sky-400/80 pointer-events-none opacity-50" 
                  aria-hidden="true"
                />

                {/* Step 1 */}
                <div className="relative flex items-start gap-3 sm:gap-4 md:gap-5 group">
                  {/* Timeline Node */}
                  <div className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#070a12] border-2 border-cyan-400 text-cyan-300 flex items-center justify-center font-bold font-mono text-[11px] sm:text-xs shadow-[0_0_14px_rgba(6,182,212,0.35)] ring-4 ring-cyan-500/10 z-10 mt-2 sm:mt-2.5">
                    01
                  </div>

                  {/* Card Content */}
                  <div className="flex-1 p-4 sm:p-5 md:py-4 md:px-5 rounded-2xl bg-slate-900/80 border border-white/10 hover:border-cyan-500/40 transition-all shadow-md">
                    {/* Top Row: Step Tag on Left, Duration Pill on Right */}
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-[11px] sm:text-xs font-mono font-bold uppercase tracking-wider text-cyan-400">
                        Step 01
                      </span>
                      <span className="text-[10px] sm:text-xs font-mono px-2.5 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-cyan-300 font-semibold whitespace-nowrap">
                        1 Minute
                      </span>
                    </div>

                    {/* Title */}
                    <h4 className="text-sm sm:text-base md:text-base font-bold text-white font-heading mb-1.5">
                      Submit Website Details
                    </h4>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      Fill out the quick form below with your website domain. Zero upfront payment required to submit.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative flex items-start gap-3 sm:gap-4 md:gap-5 group">
                  {/* Timeline Node */}
                  <div className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#070a12] border-2 border-emerald-400 text-emerald-300 flex items-center justify-center font-bold font-mono text-[11px] sm:text-xs shadow-[0_0_14px_rgba(16,185,129,0.35)] ring-4 ring-emerald-500/10 z-10 mt-2 sm:mt-2.5">
                    02
                  </div>

                  {/* Card Content */}
                  <div className="flex-1 p-4 sm:p-5 md:py-4 md:px-5 rounded-2xl bg-emerald-950/30 border border-emerald-500/40 hover:border-emerald-400/60 transition-all shadow-[0_0_20px_rgba(16,185,129,0.08)]">
                    {/* Top Row: Step Tag on Left, Duration Pill on Right */}
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-[11px] sm:text-xs font-mono font-bold uppercase tracking-wider text-emerald-400">
                        Step 02
                      </span>
                      <span className="text-[10px] sm:text-xs font-mono px-2.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-semibold whitespace-nowrap">
                        15 Min Call
                      </span>
                    </div>

                    {/* Title */}
                    <h4 className="text-sm sm:text-base md:text-base font-bold text-white font-heading mb-1.5">
                      15-Min Pre-Audit Call
                    </h4>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                      <strong className="text-emerald-400 font-semibold">Pay money only after you talk with me.</strong> Quick alignment call to understand your business model, products, and target audience before any payment.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative flex items-start gap-3 sm:gap-4 md:gap-5 group">
                  {/* Timeline Node */}
                  <div className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#070a12] border-2 border-sky-400 text-sky-300 flex items-center justify-center font-bold font-mono text-[11px] sm:text-xs shadow-[0_0_14px_rgba(56,189,248,0.35)] ring-4 ring-sky-500/10 z-10 mt-2 sm:mt-2.5">
                    03
                  </div>

                  {/* Card Content */}
                  <div className="flex-1 p-4 sm:p-5 md:py-4 md:px-5 rounded-2xl bg-slate-900/80 border border-white/10 hover:border-sky-500/40 transition-all shadow-md">
                    {/* Top Row: Step Tag on Left, Duration Pill on Right */}
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-[11px] sm:text-xs font-mono font-bold uppercase tracking-wider text-sky-400">
                        Step 03
                      </span>
                      <span className="text-[10px] sm:text-xs font-mono px-2.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/25 text-sky-300 font-semibold whitespace-nowrap">
                        24–72 Hrs
                      </span>
                    </div>

                    {/* Title */}
                    <h4 className="text-sm sm:text-base md:text-base font-bold text-white font-heading mb-1.5">
                      Deep Manual Audit
                    </h4>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      Narendra manually inspects bot accessibility, schema architecture, factual density, and compiles your Actionable Key Fixes Blueprint.
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="relative flex items-start gap-3 sm:gap-4 md:gap-5 group">
                  {/* Timeline Node */}
                  <div className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#070a12] border-2 border-emerald-400 text-emerald-300 flex items-center justify-center font-bold font-mono text-[11px] sm:text-xs shadow-[0_0_14px_rgba(16,185,129,0.35)] ring-4 ring-emerald-500/10 z-10 mt-2 sm:mt-2.5">
                    04
                  </div>

                  {/* Card Content */}
                  <div className="flex-1 p-4 sm:p-5 md:py-4 md:px-5 rounded-2xl bg-gradient-to-br from-slate-900/90 to-emerald-950/40 border border-emerald-500/35 hover:border-emerald-400/60 transition-all shadow-[0_0_20px_rgba(16,185,129,0.08)]">
                    {/* Top Row: Step Tag on Left, Duration Pill on Right */}
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-[11px] sm:text-xs font-mono font-bold uppercase tracking-wider text-emerald-400">
                        Step 04
                      </span>
                      <span className="text-[10px] sm:text-xs font-mono px-2.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-semibold whitespace-nowrap">
                        30 Min Call
                      </span>
                    </div>

                    {/* Title */}
                    <h4 className="text-sm sm:text-base md:text-base font-bold text-white font-heading mb-1.5">
                      30-Min Handover Call
                    </h4>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                      Receive Full Audit results + dedicated 30-min Google Meet session walking through fixes, checklist, and prompt packs.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================
            PREREQUISITES: WHAT WE NEED FOR AN ACCURATE AUDIT
           ========================================================= */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-4">
          <div className="bg-[#0b0f19] border border-cyan-500/30 rounded-3xl p-6 sm:p-8 relative overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/[0.08]">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-mono uppercase tracking-wider mb-2.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" />
                  <span>Audit Prerequisites • Data &amp; Access Needed</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white font-heading">
                  In order to get the accurate AI audit, I need the following things from your end:
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-1.5 leading-relaxed">
                  You don't need to share these right now. We will coordinate and walk through each item together during our 15-minute prep call. Only <strong>Viewer (read-only)</strong> access is required.
                </p>
              </div>

              <div className="shrink-0 self-start sm:self-center">
                <span className="px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs font-mono font-semibold flex items-center gap-1.5 whitespace-nowrap">
                  <Lock className="w-3.5 h-3.5 text-blue-400" />
                  <span>Read-Only Access</span>
                </span>
              </div>
            </div>

            {/* Tool Boxes Grid: 2-Column Compact Grid on Mobile, 3+2 Grid on Desktop */}
            <div className="mt-5 sm:mt-7 grid grid-cols-2 md:grid-cols-6 gap-2 sm:gap-3 lg:gap-4">
              {/* Box 1: Server LOG Files (Mobile: Col 1 | Desktop: 1/3) */}
              <div className="col-span-1 md:col-span-2 p-3 sm:p-4 md:p-5 rounded-xl sm:rounded-2xl bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-white/10 hover:border-cyan-500/40 transition-all flex flex-col justify-between group shadow-lg">
                <div>
                  {/* Header: Logo + Status Badge */}
                  <div className="flex items-center justify-between gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11 rounded-lg sm:rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center p-1 sm:p-2 shrink-0 shadow-inner group-hover:scale-105 transition-transform">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none">
                        <rect x="2" y="3" width="20" height="7" rx="2" stroke="#06B6D4" strokeWidth="1.75" fill="#06B6D4" fillOpacity="0.15"/>
                        <rect x="2" y="14" width="20" height="7" rx="2" stroke="#06B6D4" strokeWidth="1.75" fill="#06B6D4" fillOpacity="0.15"/>
                        <circle cx="6" cy="6.5" r="1.2" fill="#22D3EE"/>
                        <circle cx="9.5" cy="6.5" r="1.2" fill="#22D3EE"/>
                        <circle cx="6" cy="17.5" r="1.2" fill="#22D3EE"/>
                        <circle cx="9.5" cy="17.5" r="1.2" fill="#22D3EE"/>
                        <path d="M14 6.5h4M14 17.5h4" stroke="#22D3EE" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <span className="text-[9px] sm:text-[10px] md:text-xs font-mono px-1.5 sm:px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-cyan-300 font-semibold whitespace-nowrap">
                      Bots Only
                    </span>
                  </div>

                  <div className="text-[10px] sm:text-[11px] font-mono text-cyan-400 font-bold uppercase tracking-wider mb-0.5 sm:mb-1 truncate">
                    01 • Server Logs
                  </div>
                  <h4 className="text-xs sm:text-sm md:text-base font-bold text-white font-heading mb-1 sm:mb-1.5 leading-snug">
                    Server LOG Files
                  </h4>
                  <p className="text-[11px] sm:text-xs text-slate-300 leading-relaxed line-clamp-3 sm:line-clamp-none">
                    Raw bot crawl requests (GPTBot, ClaudeBot, Googlebot) to check if firewall rules block them.
                  </p>
                </div>

                <div className="hidden md:flex mt-4 pt-3 border-t border-white/[0.06] items-center justify-between text-[11px] text-slate-400 font-mono">
                  <span>Export / Log File</span>
                  <span className="text-cyan-400 font-semibold">Zero Upfront Risk</span>
                </div>
              </div>

              {/* Box 2: Google Search Console (Mobile: Col 2 | Desktop: 1/3) */}
              <div className="col-span-1 md:col-span-2 p-3 sm:p-4 md:p-5 rounded-xl sm:rounded-2xl bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-white/10 hover:border-blue-500/40 transition-all flex flex-col justify-between group shadow-lg">
                <div>
                  {/* Header: Logo + Status Badge */}
                  <div className="flex items-center justify-between gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11 rounded-lg sm:rounded-xl bg-blue-500/10 border border-blue-500/25 flex items-center justify-center p-1 sm:p-2 shrink-0 shadow-inner group-hover:scale-105 transition-transform">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none">
                        <rect x="2" y="3" width="20" height="18" rx="3" stroke="#4285F4" strokeWidth="1.75" fill="#4285F4" fillOpacity="0.1"/>
                        <path d="M6 15l3.5-3.5 3 3 5.5-6" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="6" cy="15" r="1.5" fill="#4285F4"/>
                        <circle cx="9.5" cy="11.5" r="1.5" fill="#34A853"/>
                        <circle cx="12.5" cy="14.5" r="1.5" fill="#FBBC05"/>
                        <circle cx="18" cy="8.5" r="2" fill="#EA4335"/>
                      </svg>
                    </div>
                    <span className="text-[9px] sm:text-[10px] md:text-xs font-mono px-1.5 sm:px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/25 text-blue-300 font-semibold whitespace-nowrap">
                      Read-Only
                    </span>
                  </div>

                  <div className="text-[10px] sm:text-[11px] font-mono text-blue-400 font-bold uppercase tracking-wider mb-0.5 sm:mb-1 truncate">
                    02 • Search Console
                  </div>
                  <h4 className="text-xs sm:text-sm md:text-base font-bold text-white font-heading mb-1 sm:mb-1.5 leading-snug">
                    Google Search Console
                  </h4>
                  <p className="text-[11px] sm:text-xs text-slate-300 leading-relaxed line-clamp-3 sm:line-clamp-none">
                    Inspect actual indexing status, search impressions, AI Overviews, and crawl errors.
                  </p>
                </div>

                <div className="hidden md:flex mt-4 pt-3 border-t border-white/[0.06] items-center justify-between text-[11px] text-slate-400 font-mono">
                  <span>Google Ecosystem</span>
                  <span className="text-blue-400 font-semibold">Read-Only Viewer</span>
                </div>
              </div>

              {/* Box 3: Google Analytics 4 (Mobile: Col 1 Row 2 | Desktop: 1/3) */}
              <div className="col-span-1 md:col-span-2 p-3 sm:p-4 md:p-5 rounded-xl sm:rounded-2xl bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-white/10 hover:border-emerald-500/40 transition-all flex flex-col justify-between group shadow-lg">
                <div>
                  {/* Header: Logo + Status Badge */}
                  <div className="flex items-center justify-between gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11 rounded-lg sm:rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center p-1 sm:p-2 shrink-0 shadow-inner group-hover:scale-105 transition-transform">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none">
                        <rect x="4" y="13" width="3.5" height="7" rx="1.75" fill="#F9AB00"/>
                        <rect x="10.25" y="8" width="3.5" height="12" rx="1.75" fill="#E37400"/>
                        <rect x="16.5" y="4" width="3.5" height="16" rx="1.75" fill="#F9AB00"/>
                        <circle cx="5.75" cy="16.5" r="1" fill="#FFFFFF" fillOpacity="0.6"/>
                      </svg>
                    </div>
                    <span className="text-[9px] sm:text-[10px] md:text-xs font-mono px-1.5 sm:px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 font-semibold whitespace-nowrap">
                      Read-Only
                    </span>
                  </div>

                  <div className="text-[10px] sm:text-[11px] font-mono text-emerald-400 font-bold uppercase tracking-wider mb-0.5 sm:mb-1 truncate">
                    03 • GA4 Analytics
                  </div>
                  <h4 className="text-xs sm:text-sm md:text-base font-bold text-white font-heading mb-1 sm:mb-1.5 leading-snug">
                    Google Analytics 4
                  </h4>
                  <p className="text-[11px] sm:text-xs text-slate-300 leading-relaxed line-clamp-3 sm:line-clamp-none">
                    Analyze AI search referral traffic paths, bounce rates, and commercial conversion paths.
                  </p>
                </div>

                <div className="hidden md:flex mt-4 pt-3 border-t border-white/[0.06] items-center justify-between text-[11px] text-slate-400 font-mono">
                  <span>Traffic &amp; Conversions</span>
                  <span className="text-emerald-400 font-semibold">Read-Only Viewer</span>
                </div>
              </div>

              {/* Box 4: Bing Webmaster (Mobile: Col 2 Row 2 | Desktop: 1/2) */}
              <div className="col-span-1 md:col-span-3 p-3 sm:p-4 md:p-5 rounded-xl sm:rounded-2xl bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-white/10 hover:border-amber-500/40 transition-all flex flex-col justify-between group shadow-lg">
                <div>
                  {/* Header: Logo + Status Badge */}
                  <div className="flex items-center justify-between gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    <div className="w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11 rounded-lg sm:rounded-xl bg-teal-500/10 border border-teal-500/25 flex items-center justify-center p-1 sm:p-2 shrink-0 shadow-inner group-hover:scale-105 transition-transform">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none">
                        <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1" fill="#F25022"/>
                        <rect x="13" y="3.5" width="7.5" height="7.5" rx="1" fill="#7FBA00"/>
                        <rect x="3.5" y="13" width="7.5" height="7.5" rx="1" fill="#00A4EF"/>
                        <rect x="13" y="13" width="7.5" height="7.5" rx="1" fill="#FFB900"/>
                      </svg>
                    </div>
                    <span className="text-[9px] sm:text-[10px] md:text-xs font-mono px-1.5 sm:px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-300 font-semibold whitespace-nowrap">
                      If Available
                    </span>
                  </div>

                  <div className="text-[10px] sm:text-[11px] font-mono text-amber-400 font-bold uppercase tracking-wider mb-0.5 sm:mb-1 truncate">
                    04 • Bing &amp; Copilot
                  </div>
                  <h4 className="text-xs sm:text-sm md:text-base font-bold text-white font-heading mb-1 sm:mb-1.5 leading-snug">
                    Bing Search Console
                  </h4>
                  <p className="text-[11px] sm:text-xs text-slate-300 leading-relaxed line-clamp-3 sm:line-clamp-none">
                    Powers Microsoft Copilot AI search indexing. Shows Copilot bot crawls and visibility.
                  </p>
                </div>

                <div className="hidden md:flex mt-4 pt-3 border-t border-white/[0.06] items-center justify-between text-[11px] text-slate-400 font-mono">
                  <span>Microsoft Copilot AI</span>
                  <span className="text-amber-400 font-semibold">Optional</span>
                </div>
              </div>

              {/* Box 5: Microsoft Clarity (Mobile: Full Width 2 Cols | Desktop: 1/2) */}
              <div className="col-span-2 md:col-span-3 p-3 sm:p-4 md:p-5 rounded-xl sm:rounded-2xl bg-gradient-to-b from-slate-900/90 to-slate-950/90 border border-white/10 hover:border-purple-500/40 transition-all flex flex-col justify-between group shadow-lg">
                <div>
                  {/* Header: Logo + Status Badge */}
                  <div className="flex items-center justify-between gap-2 mb-2 sm:mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11 rounded-lg sm:rounded-xl bg-purple-500/10 border border-purple-500/25 flex items-center justify-center p-1 sm:p-2 shrink-0 shadow-inner group-hover:scale-105 transition-transform">
                        <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none">
                          <circle cx="12" cy="12" r="8.5" stroke="#A855F7" strokeWidth="1.75" fill="#A855F7" fillOpacity="0.08"/>
                          <circle cx="12" cy="12" r="3.5" fill="#C084FC"/>
                          <circle cx="16.5" cy="8.5" r="1.5" fill="#EC4899"/>
                          <circle cx="7.5" cy="15.5" r="1.5" fill="#06B6D4"/>
                          <path d="M12 5.5v2M12 16.5v2M5.5 12h2M16.5 12h2" stroke="#A855F7" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
                        </svg>
                      </div>
                      <div>
                        <div className="text-[10px] sm:text-[11px] font-mono text-purple-400 font-bold uppercase tracking-wider">
                          05 • Heatmaps
                        </div>
                        <h4 className="text-xs sm:text-sm md:text-base font-bold text-white font-heading leading-tight">
                          Microsoft Clarity <span className="text-[10px] sm:text-xs font-normal text-slate-400 font-mono">(Optional)</span>
                        </h4>
                      </div>
                    </div>
                    <span className="text-[9px] sm:text-[10px] md:text-xs font-mono px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/25 text-purple-300 font-semibold whitespace-nowrap shrink-0">
                      Optional
                    </span>
                  </div>

                  <p className="text-[11px] sm:text-xs text-slate-300 leading-relaxed">
                    Optional behavioral session recordings and heatmaps to reveal where visitors get stuck, scroll depth, and interaction drop-off.
                  </p>
                </div>

                <div className="hidden md:flex mt-4 pt-3 border-t border-white/[0.06] items-center justify-between text-[11px] text-slate-400 font-mono">
                  <span>UX &amp; Heatmap Recordings</span>
                  <span className="text-purple-400 font-semibold">Optional</span>
                </div>
              </div>
            </div>

            {/* Security Guarantee Strip */}
            <div className="mt-5 p-3.5 rounded-xl bg-emerald-950/30 border border-emerald-500/30 flex items-center gap-3 text-xs text-slate-300">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>
                <strong className="text-white">Strict Privacy &amp; Security:</strong> Viewer-level access gives read-only visibility—no one can ever modify your settings or site code. All data is kept 100% confidential.
              </span>
            </div>
          </div>
        </section>

        {/* =========================================================
            ORDER & INTAKE FORM SECTION
           ========================================================= */}
        <section id="order-audit-form" className="max-w-4xl mx-auto px-3.5 sm:px-6 lg:px-8 py-10 sm:py-16 scroll-mt-24">
          <div className="bg-gradient-to-b from-[#0b0f19] to-[#070a12] border-2 border-cyan-500/30 rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-10 shadow-[0_0_50px_rgba(6,182,212,0.15)] relative overflow-hidden">
            {/* Corner Glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

            {/* Header */}
            <div className="text-center max-w-2xl mx-auto pb-8 border-b border-white/[0.08]">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-mono uppercase tracking-wider mb-3">
                <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
                <span>Instant Intake • ₹5,000 Flat</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-heading">
                Order Your Website AI Visibility Audit
              </h2>
              <p className="text-sm sm:text-base text-slate-300 mt-2">
                Takes 60 seconds. Guaranteed 72-hour delivery + includes your <span className="text-cyan-300 font-semibold">Key Fixes Blueprint & Free Content Plan</span>.
              </p>
            </div>

            {/* Form Content / Success State */}
            {isSubmitted ? (
              <div className="py-10 text-center space-y-6 animate-in fade-in zoom-in-95 duration-200">
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                  <CheckCircle2 className="w-8 h-8" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-white font-heading">
                    Audit Request Received!
                  </h3>
                  <p className="text-sm text-slate-300 max-w-md mx-auto leading-relaxed">
                    Thank you, <span className="text-white font-semibold">{formData.name}</span>! Narendra has received your domain <span className="text-cyan-300 font-mono">({formData.website})</span>.
                  </p>
                </div>

                {/* Next Steps Box */}
                <div className="max-w-md mx-auto p-5 rounded-2xl bg-slate-900/90 border border-white/10 text-left text-xs space-y-3 font-mono text-slate-300">
                  <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase text-[11px]">
                    <Clock className="w-4 h-4" />
                    <span>What Happens in the Next 2 Hours:</span>
                  </div>
                  <p>1. Narendra personally inspects your domain details.</p>
                  <p>2. You receive an official confirmation and secure ₹5,000 payment details via WhatsApp/Email.</p>
                  <p>3. Your 72-hour delivery countdown starts immediately upon payment verification.</p>
                </div>

                {/* Instant WhatsApp Priority Button */}
                <div className="pt-2">
                  <a
                    href={`https://wa.me/918464094525?text=${encodeURIComponent(
                      `Hi Narendra, I just submitted my domain ${formData.website} for the ₹5,000 Website AI Visibility Audit. Looking forward to connecting!`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold text-sm shadow-[0_0_25px_rgba(16,185,129,0.4)] transition-all transform hover:-translate-y-0.5"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>Ping Narendra on WhatsApp for Fast Processing</span>
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="mt-8 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-2">
                      Your Full Name <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 text-white text-sm placeholder:text-slate-500 transition-colors outline-none"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-2">
                      Email Address <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="e.g. rahul@gmail.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 text-white text-sm placeholder:text-slate-500 transition-colors outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Website URL */}
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-2">
                      Website URL to Audit <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="website"
                      required
                      placeholder="e.g. https://yourcompany.com"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 text-white text-sm placeholder:text-slate-500 transition-colors outline-none font-mono"
                    />
                  </div>

                  {/* WhatsApp / Phone */}
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-2">
                      WhatsApp / Phone Number <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="tel"
                      name="whatsapp"
                      required
                      placeholder="e.g. +91 98765 43210"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 text-white text-sm placeholder:text-slate-500 transition-colors outline-none font-mono"
                    />
                    <span className="text-[10px] text-slate-400 mt-1 block">For 72-hour delivery link & quick updates</span>
                  </div>
                </div>

                {/* Primary Service / Product */}
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-2">
                    What is your core service or product? <span className="text-cyan-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="serviceNiche"
                    required
                    placeholder="e.g. B2B SaaS for HR automation / Enterprise Cybersecurity solutions"
                    value={formData.serviceNiche}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 text-white text-sm placeholder:text-slate-500 transition-colors outline-none"
                  />
                </div>

                {/* Specific Questions / Notes */}
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-2">
                    Any specific questions or areas you want examined? <span className="text-slate-500">(Optional)</span>
                  </label>
                  <textarea
                    name="specificQuestion"
                    rows="3"
                    placeholder="e.g. We have high traffic on Google but don't know if our website is structured so LLMs can understand our offerings."
                    value={formData.specificQuestion}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 text-white text-sm placeholder:text-slate-500 transition-colors outline-none resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-heading font-bold text-base shadow-[0_0_35px_rgba(37,99,235,0.4)] hover:shadow-[0_0_45px_rgba(6,182,212,0.6)] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group mt-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Submitting AI Visibility Audit Request...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit & Order AI Visibility Audit — ₹5,000</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>

                {/* Reassurance & Privacy Strip */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2.5 text-center text-xs text-slate-400 font-mono pt-1">
                  <span className="flex items-center gap-1.5 text-emerald-400 font-medium">
                    <Lock className="w-3.5 h-3.5 shrink-0" />
                    <span>Zero upfront payment • Pay only after we talk</span>
                  </span>
                  <span className="hidden sm:inline text-slate-600">•</span>
                  <span>100% confidential • Confirmed within 2 hours</span>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* =========================================================
            FREQUENTLY ASKED QUESTIONS (FAQ)
           ========================================================= */}
        <section className="max-w-4xl mx-auto px-3.5 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-heading">
              Frequently Asked Questions
            </h2>
            <p className="text-sm text-slate-400 mt-2">
              Everything you need to know about the ₹5,000 Website AI Visibility Audit
            </p>
          </div>

          <div className="space-y-3">
            {[
              {
                q: "What will I get with this audit?",
                a: (
                  <div className="space-y-2">
                    <p>You receive the complete 6-part deliverable package tailored specifically to your website and products:</p>
                    <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                      <li><strong className="text-white">Full Website AI Checkup</strong> – Shows exactly why ChatGPT, Claude, and Gemini aren't citing your brand and provides step-by-step developer fixes.</li>
                      <li><strong className="text-white">Top Product Content Topics</strong> – High-intent topics custom-built for what you sell.</li>
                      <li><strong className="text-white">Two 1-on-1 Calls</strong> – 15-minute prep call + dedicated 30-minute Google Meet handover walkthrough.</li>
                      <li><strong className="text-white">Pre-Publishing Checklist</strong> – Easy team standard for your writers before publishing anything new.</li>
                      <li><strong className="text-white">Content Discovery &amp; AI Research Prompts</strong> – Reusable prompts to mine ideas and test visibility independently.</li>
                      <li><strong className="text-white">20 to 30 Growth Tracking Prompts</strong> – Monthly benchmarks to monitor your visibility growth in LLMs over time.</li>
                    </ul>
                  </div>
                )
              },
              {
                q: "Can I see a sample or demo audit report before ordering?",
                a: (
                  <div className="space-y-2">
                    <p>
                      Yes! You can download our complete 10-slide <strong className="text-white">Demo AI Visibility Audit Presentation (.pptx)</strong> directly on this page to see the exact diagnostic format, baseline scorecard, developer-ready fix blueprints, and 30-prompt tracking matrix we deliver.
                    </p>
                    <p>
                      You can also preview each slide interactively in the <a href="#demo-audit-report" className="text-cyan-400 underline underline-offset-2 hover:text-cyan-300">Demo Audit section</a> above.
                    </p>
                  </div>
                )
              },
              {
                q: "How to pay?",
                a: "You can pay directly to Narendra via PhonePe (UPI / QR Code / Bank transfer). Payment details are provided once we connect and confirm your website intake. Remember: you only pay after we talk and align on your website goals."
              },
              {
                q: "How long will it take to give results?",
                a: "Guaranteed 72-hour turnaround. Your 72-hour countdown begins right after our 15-minute prep call is completed and intake is confirmed."
              },
              {
                q: "How to give access to tools?",
                a: "Will discuss in mail. You only need to share read-only (Viewer) access to Google Search Console, GA4, or server logs—no admin permissions needed. We will walk you through the simple steps over email or during our prep call."
              },
              {
                q: "How will the 15-minute call happen?",
                a: "I will send a Google Meet invite link to your email/WhatsApp. We will coordinate a quick, convenient 15-minute slot to understand your core products, target audience, and business focus."
              },
              {
                q: "Will I get an invoice?",
                a: "Yes, a basic invoice will be provided for your accounting and business records."
              },
              {
                q: "If I have any doubts in the report?",
                a: "You can connect with me directly through phone or WhatsApp (+91 8464094525). I will connect right away. If I am busy on another client call, I will promptly drop you a Google Meet invite link to walk through your questions."
              },
              {
                q: "Do you implement the fixes on my website, or do I?",
                a: "I provide the complete diagnostic report, prioritized action plan, and exact developer instructions & Schema templates, and walk through them on our 30-minute Google Meet call. Your developer can implement the changes quickly using my step-by-step guide."
              }
            ].map((faq, idx) => (
              <div
                key={idx}
                className="rounded-2xl bg-slate-900/70 border border-white/10 overflow-hidden transition-colors"
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(idx)}
                  className="w-full p-5 text-left flex items-center justify-between gap-4 focus:outline-none"
                >
                  <span className="text-sm sm:text-base font-bold text-white font-heading">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-cyan-400 shrink-0 transition-transform duration-200 ${
                      openFaq === idx ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-5 text-xs sm:text-sm text-slate-300 leading-relaxed border-t border-white/5 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* =========================================================
            FINAL DIRECT CONTACT CALLOUT
           ========================================================= */}
        <section className="max-w-4xl mx-auto px-3.5 sm:px-6 lg:px-8 py-6 sm:py-10 text-center">
          <div className="p-5 sm:p-8 rounded-2xl sm:rounded-3xl bg-slate-950 border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="text-left">
              <h3 className="text-xl font-bold text-white font-heading">
                Have questions before ordering?
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 mt-1">
                Chat directly with Narendra on WhatsApp or phone: <strong className="text-white">+91 8464094525</strong>
              </p>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <a
                href="https://wa.me/918464094525?text=Hi%20Narendra,%20I%20have%20a%20question%20regarding%20the%20Website%20AI%20Visibility%20Audit."
                target="_blank"
                rel="noreferrer"
                className="px-5 py-2.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 font-mono text-xs font-bold transition-colors flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                <span>WhatsApp Narendra</span>
              </a>

              <button
                onClick={copyPhoneNumber}
                className="p-2.5 rounded-xl bg-slate-900 border border-white/10 text-slate-300 hover:text-white transition-colors"
                title="Copy phone number"
                aria-label="Copy phone number"
              >
                {copiedPhone ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* =========================================================
          STICKY BOTTOM BAR (Mobile & Desktop)
         ========================================================= */}
      <AnimatePresence>
        {showStickyBar && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-4 left-4 right-4 z-40 max-w-4xl mx-auto"
          >
            <div className="p-3.5 sm:p-4 rounded-2xl bg-[#0b0f19]/95 backdrop-blur-xl border border-cyan-500/40 shadow-2xl shadow-cyan-950/60 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="truncate">
                  <p className="text-xs sm:text-sm font-bold text-white truncate">
                    Website AI Visibility Audit + Key Fixes
                  </p>
                  <p className="text-[11px] text-cyan-300 font-mono truncate">
                    ₹5,000 Flat · 72-Hour Turnaround · Includes Free Content Plan
                  </p>
                </div>
              </div>

              <button
                onClick={scrollToForm}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-heading font-bold text-xs sm:text-sm shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all shrink-0 cursor-pointer"
              >
                Order Audit — ₹5,000
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
